/**
 * JOINING THE POLICY'S DECISIONS ONTO THE AWAITING-REVIEW QUEUE.
 *
 * The queue is DynamoDB sync rows; a decision is a Mongo record. They join on
 * the partner's own reference — the decision calls it `reference`, the sync row
 * calls it `BookingId`, and that equality is the only thing connecting the two
 * stores (the same equality `findQueueRow` in ./syncQueue relies on from the
 * other side).
 *
 * ── WHY THIS IS ONE REQUEST AND A CLIENT-SIDE JOIN ──
 *
 * `GET /partner-integrations/decisions` takes `reference` as a SINGLE value —
 * `buildDecisionQuery` sets `query.reference = filters.reference`, with no
 * `$in` — so "a decision per row" would be one request per card, twenty
 * requests to draw one screen, and a queue that gets slower the more work is
 * waiting on it. Instead: one page of decisions over a window that provably
 * covers the queue, joined here.
 *
 * ── THE WINDOW IS DERIVED FROM THE QUEUE, NOT GUESSED ──
 *
 * A decision about an offer cannot predate the offer, and `createdAt` on a sync
 * row is when the offer arrived (epoch MILLISECONDS — `Date.now()` at every
 * creation site, and the schema's rangeKey, so it is never absent). So the
 * oldest `createdAt` on the queue is an exact lower bound for every decision
 * that could possibly belong to a row on it. A fixed "last 7 days" would
 * silently miss the decision on a three-week-old pending offer and render it as
 * undecided, which is the one mistake this screen must not make.
 *
 * ── THREE STATES, NEVER TWO ──
 *
 * `decided`  the policy ruled on this offer. Newest ruling wins; see below.
 * `unscored` the pass has not reached it yet. NOT a clearance — nothing has
 *            been checked. An arriving offer asks for a pass of its own, so
 *            this state is usually brief — but a trigger that did not fire
 *            leaves an offer here until the daily safety-net sweep, which is
 *            exactly the case that must not read as "nothing wrong". So it
 *            reads as "not looked at", however long it has been.
 * `unknown`  we could not find out: the request failed, or the page was
 *            truncated at the endpoint's cap so an absent match proves
 *            nothing. Said as ignorance, because dressing ignorance as
 *            "not scored yet" would be a claim about the policy.
 */

import { isAbsent } from './presentation';
import { byDecidedAtDesc } from './AutoApproval/decisionFacts';
import {
  fetchPartnerDecisions,
  fetchPartnerDecisionSummary,
} from '../../Services/PartnerIntegrationService';
// The 30-day override window and the ISO helper are the decision screens'
// own, and the counters on this page count the same things over the same
// windows. Imported rather than restated: two spellings of "30 days" is two
// numbers to keep in step for the sake of one line.
import { DISAGREEMENT_WINDOW_DAYS, sinceIso } from './AutoApproval/useDecisionFeed';

export const QUEUE_DECISION_STATE = {
  DECIDED: 'decided',
  UNSCORED: 'unscored',
  UNKNOWN: 'unknown',
};

/**
 * THE WORDS FOR THE TWO STATES THAT HAVE NO DECISION — and the whole reason
 * they are two states.
 *
 * Neither says anything good about the offer, and neither borrows the shape of
 * a verdict: no green, no "cleared", no tick. The badges are grey because grey
 * is what "we have not got an answer" looks like, and the sentence under each
 * one says which kind of not-having-an-answer it is. An operator who reads
 * either of these and then reviews the booking exactly as they did last month
 * has done the right thing.
 *
 * `auto_approved` would be a green badge here, and that is precisely why
 * "nothing is recorded" must never be allowed to reach for the same slot.
 */
export const QUEUE_DECISION_ABSENCE = {
  [QUEUE_DECISION_STATE.UNSCORED]: {
    badge: { label: 'Not scored yet', bg: 'light', text: 'dark' },
    copy: 'The policy has not reached this offer yet — a pass is started when an offer arrives, and no pass has scored this one. Nothing has been checked against the policy, so this is not a clearance. Run now starts a pass if it has been sitting.',
  },
  [QUEUE_DECISION_STATE.UNKNOWN]: {
    badge: { label: 'Decision not loaded', bg: 'secondary' },
    copy: 'We could not read the decision log just now, so whether the policy has scored this offer is unknown. That is a fact about us, not about the booking.',
  },
};

/**
 * The endpoint's own cap. `MAX_LIMIT` in `SyncDecisionQueryService` is 200 and
 * a larger number is clamped there, so asking for more would only make the
 * truncation harder to notice.
 */
export const QUEUE_DECISION_LIMIT = 200;

/**
 * The window the counter strip summarises. FIXED at 24 hours because this page
 * has no window picker — the filters belong to the log, which is the screen for
 * asking a different question. A card labelled "today" over an unknown window
 * is a wrong number with a confident label.
 */
export const QUEUE_COUNTER_WINDOW_DAYS = 1;

/**
 * The earliest moment a decision on today's queue could have been taken, as
 * fixed-width UTC ISO — or `undefined` for an empty queue (no rows, nothing to
 * join, and `definedParams` drops the filter).
 */
export const sinceForQueue = (rows = []) => {
  const stamps = (Array.isArray(rows) ? rows : [])
    .map((row) => Number(row?.createdAt))
    .filter((at) => Number.isFinite(at) && at > 0);

  if (stamps.length === 0) return undefined;

  return new Date(Math.min(...stamps)).toISOString();
};

/**
 * Reference → the NEWEST decision for it.
 *
 * One reference carries several decisions on purpose: the pass re-scores an
 * offer nobody has acted on, so a row that has been waiting carries one
 * decision per pass that looked at it — every arriving offer starts a pass,
 * and the daily sweep starts one too. The newest is the live one — an older
 * hold may name a
 * reason that has since stopped applying (a duplicate that was cleaned up, a
 * quote that now resolves), and showing it would send somebody to chase a
 * problem that no longer exists.
 */
export const newestDecisionByReference = (rows = []) => {
  const byReference = new Map();

  (Array.isArray(rows) ? rows : [])
    .filter((row) => row && !isAbsent(row.reference))
    .forEach((row) => {
      const key = String(row.reference);
      const held = byReference.get(key);
      if (!held || byDecidedAtDesc(row, held) < 0) byReference.set(key, row);
    });

  return byReference;
};

/**
 * Turn one page of decisions into "what do we know about this reference".
 *
 * `failed` and `total > rows.length` both mean an absent match proves nothing,
 * and they are folded into one `unknown` state deliberately: the row's copy
 * says we could not find out, which is true of both, and a reader does not need
 * to know which of our two requests let them down.
 */
export const decisionLookup = (feed = null) => {
  // NO FEED AT ALL IS IGNORANCE, not an empty window — so `decisionLookup()`
  // defaults to `failed`. A screen that has not read anything yet (first
  // paint) must not spend that moment answering "the policy has not scored
  // this" for every row on the queue; an explicit `{ rows: [], total: 0 }` is
  // a read that came back empty, and that one does mean unscored.
  const { rows = [], total = 0, failed = feed === null } = feed || {};
  const usable = (Array.isArray(rows) ? rows : [])
    .filter((row) => row && !isAbsent(row.reference));
  const byReference = newestDecisionByReference(usable);
  // Measured against RECORDS RECEIVED, not against references: `total` counts
  // records and the pass writes several per reference, so a complete page of
  // 12 records over 5 references must not read as truncated.
  const complete = !failed && Number(total) <= usable.length;

  return (reference) => {
    const decision = byReference.get(String(reference)) || null;

    if (decision) return { state: QUEUE_DECISION_STATE.DECIDED, decision };

    return {
      state: complete ? QUEUE_DECISION_STATE.UNSCORED : QUEUE_DECISION_STATE.UNKNOWN,
      decision: null,
    };
  };
};

/**
 * ONE MARGIN PER CARD, from whichever of the two records measured it.
 *
 * They are the SAME QUANTITY: `features.margin` on a decision is
 * `ISyncProfitAnalysis.profit_pct` — the API interface says so in as many words
 * — and `ProfitAnalysis.profit_pct` on the sync row is that same field,
 * computed by the same analyzer at ingestion. So rendering both would be the
 * two-lists problem in miniature: one number, printed twice, inviting a reader
 * to look for a difference that means nothing.
 *
 * The sync row's own analysis comes first because it is the row's own record
 * and it is what this card has always shown. The decision's features are the
 * FALLBACK, and they are a real gain: an offer whose pricing failed at
 * ingestion carries no `ProfitAnalysis` at all, and until now that card showed
 * no margin, no prices and no explanation. If the policy later managed to price
 * it, the card can now say so.
 *
 * Every field stays nullable all the way through. `pct: null` is the honest
 * answer for an offer nobody could price, and the caller dashes it — a `0`
 * here would tone as a loss on a screen somebody accepts transfers from.
 */
export const marginForCard = (booking = {}, decision = null) => {
  const analysis = booking?.ProfitAnalysis;

  if (analysis && !isAbsent(analysis.profit_pct)) {
    return {
      pct: analysis.profit_pct,
      our: analysis.our_price,
      ourCurrency: analysis.our_currency,
      offer: analysis.partner_offer,
      offerCurrency: analysis.partner_currency,
    };
  }

  const features = decision?.features || {};

  return {
    pct: isAbsent(features.margin) ? null : features.margin,
    our: isAbsent(features.our_price) ? null : features.our_price,
    ourCurrency: features.currency || null,
    offer: isAbsent(features.offer) ? null : features.offer,
    offerCurrency: features.currency || null,
  };
};

/**
 * Everything the queue needs from the decisions endpoint, in one pass.
 *
 * THE THREE READS DEGRADE SEPARATELY, and none of them can take the queue
 * down — the queue is the screen, and a partner booking still has to be
 * approved when Mongo is having a bad afternoon:
 *
 *   the decision page  → `lookup` answers `unknown` for every row, and the
 *                        rows say so instead of claiming nothing was scored.
 *   the summary        → the counter strip's numbers go to zero-with-no-share,
 *                        which `CounterCards` already renders as "nothing
 *                        scored" rather than as 0%.
 *   the override count → `null`, which that card renders as a dash and "could
 *                        not be counted". Never 0, which reads as agreement.
 *
 * The override count is its own request because its window is fixed at 30 days
 * while the strip's is 24 hours — the same reason `useDecisionFeed` asks for it
 * separately. `limit: 1` because only `total` is wanted.
 */
export const fetchQueueDecisionContext = async (queueRows = []) => {
  const [page, summary, overridden] = await Promise.all([
    fetchPartnerDecisions({
      since: sinceForQueue(queueRows),
      limit: QUEUE_DECISION_LIMIT,
    }).catch(() => null),
    fetchPartnerDecisionSummary({
      since: sinceIso(QUEUE_COUNTER_WINDOW_DAYS),
    }).catch(() => null),
    fetchPartnerDecisions({
      overridden: true,
      since: sinceIso(DISAGREEMENT_WINDOW_DAYS),
      limit: 1,
    }).catch(() => null),
  ]);

  return {
    lookup: decisionLookup(page ? { ...page, failed: false } : { failed: true }),
    rows: page?.rows || [],
    summary: summary || null,
    disagreements: overridden ? overridden.total : null,
  };
};
